import { Transition } from "@headlessui/react";
import { ContractSuretyType, SuretyQuoteStatus } from "@inrev/common";
import { type SuretyAccount } from "@portal/api/account";
import { useFetchAccountSpecialtyCodes } from "@portal/api/account/agent";
import { useFetchBond } from "@portal/api/bond/agent";
import { bondRequestAttachmentsQueryKey, bondRequestsQueryKey } from "@portal/api/bond-request";
import { BondRequest } from "@portal/api/bond-request/agent";
import { useQuoteComments } from "@portal/api/comments";
import { useQuoteFollowers } from "@portal/api/followers";
import { useFetchQuoteAttachments } from "@portal/api/quote";
import { AgentBondFormSection } from "@portal/components/features/bond-form";
import { messageDraftKey } from "@portal/components/features/message-draft";
import { CommentsSection } from "@portal/components/layout/CommentsSection";
import { cn } from "@portal/components/lib/utils";
import { Card } from "@portal/components/primitives";
import { BondRequestAttachmentsTable } from "@portal/components/shared/attachments/BondRequestAttachmentsTable";
import { useHeaderCollapse } from "@portal/hooks/useHeaderCollapse";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import {
	type ReportScrolledFromTop,
	useScrolledFromTopGestures,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { type QueryKey } from "@tanstack/react-query";
import {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type WheelEvent,
} from "react";
import { AgentWorkflowLayout } from "../../agent/layout/AgentWorkflowLayout";
import { BondDetailsSection } from "../../agent/shared/BondDetailsSection";
import { AgentBondRequestStickyPanel } from "./AgentBondRequestStickyPanel";
import BondFilesUpload from "./BondFilesUpload";
import { BondRequestCardHeader } from "./BondRequestCardHeader";
import { BondRequestTermsAndConditions } from "./BondRequestTermsAndConditions";
import { BondRequestTitle } from "./BondRequestTitle";

type BondRequestLayoutProps = {
	request: BondRequest;
	account: SuretyAccount;
	banner?: ReactNode;
	actionButtons?: ReactNode;
	termsAndConditionsOpen: boolean;
	headerNotice?: ReactNode;
	cardBottom?: ReactNode;
	children?: ReactNode;
};

const ENTITY_VIEW_COLUMN_CLASS = "w-full max-w-[min(950px,calc(100%-96px))] mx-auto";

const canDeleteBondAttachments = (status: SuretyQuoteStatus) =>
	status !== SuretyQuoteStatus.approved &&
	status !== SuretyQuoteStatus.accepted &&
	status !== SuretyQuoteStatus.pending &&
	status !== SuretyQuoteStatus.closed;

const QuoteBondFilesSection = memo(
	({
		request,
		invalidateQueryKeys,
	}: {
		request: BondRequestLayoutProps["request"];
		invalidateQueryKeys: QueryKey[];
	}) => {
		const { attachments, attachmentsIsLoading } = useFetchQuoteAttachments(
			request.suretyAccountId,
			request.id,
		);
		const [localAttachments, setLocalAttachments] = useState(attachments);

		useEffect(() => {
			setLocalAttachments(attachments);
		}, [attachments]);

		return (
			<BondFilesUpload
				bondType={request.contractSuretyType}
				attachments={localAttachments}
				onChange={setLocalAttachments}
				accountId={request.suretyAccountId}
				allowDelete={canDeleteBondAttachments(request.status)}
				baseUrl={`/v2/surety/accounts/${request.suretyAccountId}/quotes/${request.id}`}
				allowUpload={request.status !== SuretyQuoteStatus.closed}
				additionalInvalidateQueryKeys={invalidateQueryKeys}
				isLoading={attachmentsIsLoading}
			/>
		);
	},
);

export const BondRequestLayout = ({
	request,
	account,
	banner,
	actionButtons,
	termsAndConditionsOpen,
	headerNotice,
	cardBottom,
	children,
}: BondRequestLayoutProps) => {
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const { comments, commentsLoading, createComment, createCommentLoading } = useQuoteComments(
		request.suretyAccountId,
		request.id,
	);
	const followersControl = useQuoteFollowers(request.suretyAccountId, request.id);
	const { headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } = useHeaderCollapse();
	const { rows: specialtyRows } = useFetchAccountSpecialtyCodes(account.id);
	const accountNaicsCodes = specialtyRows.map((row) => row.id);
	const { bond: bidBond } = useFetchBond(
		(() => {
			if (request.contractSuretyType === ContractSuretyType.bid_to_final) return request.bidBondId;
			return "";
		})(),
		request.contractSuretyType === ContractSuretyType.bid_to_final,
	);

	const invalidateQueryKeys = useMemo(
		() => [bondRequestAttachmentsQueryKey(request.id), bondRequestsQueryKey(request.id)],
		[request.id],
	);
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const reportScrolledFromTop = useCallback<ReportScrolledFromTop>(
		(scrolledFromTop) => setHeaderCollapsed(scrolledFromTop),
		[setHeaderCollapsed],
	);

	const {
		onScroll: handlePageScroll,
		attachNativeWheel: attachPageWheel,
		decideWheel: decideChromeWheel,
	} = useScrolledFromTopGestures(reportScrolledFromTop, undefined, getHeaderCollapsed);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current = node ? attachPageWheel(node) : undefined;
		},
		[attachPageWheel],
	);

	const handleChromeWheel = (e: WheelEvent<HTMLDivElement>) => {
		const target = scrollRef.current;
		if (!target || (e.target instanceof Node && target.contains(e.target))) return;
		const decision = decideChromeWheel(target, e);
		if (decision.collapse) {
			reportScrolledFromTop(true);
			return;
		}
		if (decision.block) return;
		if (decision.expand) reportScrolledFromTop(false);
		target.scrollBy({ top: e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY });
	};

	const showTerms =
		request.status !== SuretyQuoteStatus.declined && request.status !== SuretyQuoteStatus.review;

	return (
		<AgentWorkflowLayout
			title={<BondRequestTitle request={request} bidBond={bidBond} />}
			contentClassName="bg-surface-light"
			hideActionButtons
		>
			<div
				className="w-full h-full flex flex-col min-h-0"
				onWheel={handleChromeWheel}
				data-testid="agent-bond-request-entity-view-layout"
			>
				<div
					data-testid="agent-bond-request-header-wrapper"
					className={cn(
						"shrink-0 grid bg-surface-base transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none",
						headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
					)}
				>
					<div className="overflow-hidden min-h-0">
						<div
							id="bond_request_card"
							className={cn(
								ENTITY_VIEW_COLUMN_CLASS,
								"flex flex-col space-y-[16px] pt-[18px]",
								headerNotice ? "pb-[10px]" : "pb-[18px]",
							)}
						>
							<BondRequestCardHeader
								request={request}
								account={account}
								followersControl={followersControl}
								actionButtons={actionButtons}
								className="pb-0"
							/>
							{headerNotice}
							{showTerms && (
								<Transition
									show={termsAndConditionsOpen}
									as="div"
									enter="transition-all ease-in duration-[90ms]"
									enterFrom="h-0 opacity-50"
									enterTo="h-fit h-min-fit opacity-100"
									leave="transition-all ease-in duration-75"
									leaveFrom="opacity-0 h-fit min-h-fit"
									leaveTo="opacity-0 h-0"
								>
									<BondRequestTermsAndConditions request={request} />
								</Transition>
							)}
						</div>
					</div>
				</div>

				<div
					data-testid={headerCollapsed ? "agent-bond-request-collapsed-chrome" : undefined}
					className={cn(
						"bg-surface-base shrink-0 z-[25] border-b border-b-bc-subtle",
						headerCollapsed && "flex items-center justify-end px-8 py-2 shadow-md shadow-gray-50",
					)}
				>
					{headerCollapsed ? (
						<AgentBondRequestStickyPanel request={request} followersControl={followersControl} />
					) : null}
				</div>

				<div
					data-testid="agent-bond-request-scroll-container"
					ref={setScrollRef}
					className="flex-1 min-h-0 overflow-auto overscroll-y-contain"
					onScroll={handlePageScroll}
				>
					{/* Top inset rides on the column, not the scroll container: padding on a scroll
					    container insets the sticky constraint rect, which would park the sections'
					    sticky edit headers below the scrollport top and let rows scroll past above them. */}
					<div
						className={cn(ENTITY_VIEW_COLUMN_CLASS, "flex flex-col space-y-[40px] mt-6 pb-[125px]")}
					>
						{banner}
						{cardBottom && (
							<Card
								elevation="raised"
								className="rounded-xl"
								data-testid="agent-bond-request-status-card"
							>
								<Card.Body className="p-6">{cardBottom}</Card.Body>
							</Card>
						)}
						{children}

						{isMinimalUIEnabled ? (
							<>
								<BondDetailsSection request={request} accountNaicsCodes={accountNaicsCodes} />
								<AgentBondFormSection request={request} />
							</>
						) : (
							<>
								<AgentBondFormSection request={request} />
								<QuoteBondFilesSection
									request={request}
									invalidateQueryKeys={invalidateQueryKeys}
								/>
								<BondDetailsSection request={request} accountNaicsCodes={accountNaicsCodes} />
							</>
						)}

						<Card
							elevation="raised"
							className="rounded-xl"
							data-testid="agent-bond-request-messages-card"
						>
							<Card.Body className="p-6">
								<CommentsSection
									entityType="Bond"
									exportEntityType="bondRequest"
									comments={comments}
									commentsLoading={commentsLoading}
									createComment={createComment}
									createCommentLoading={createCommentLoading}
									draftKey={messageDraftKey("quotes", request.id)}
									commentDownloadHeader={`${request.displayTitle} For ${request.principal.name}`}
									downloadTrackingId="bond-request-agent-comments-download"
								/>
							</Card.Body>
						</Card>
						<BondRequestAttachmentsTable
							accountId={request.suretyAccountId}
							quoteId={request.id}
							bondType={request.contractSuretyType}
							allowDelete={isMinimalUIEnabled && canDeleteBondAttachments(request.status)}
							allowUpload={request.status !== SuretyQuoteStatus.closed}
							downloadAllName={`${account.displayName} ${request.displayTitle} Attachments`}
						/>
					</div>
				</div>
			</div>
		</AgentWorkflowLayout>
	);
};
